import { useEffect, useRef, type RefObject } from "react";

/**
 * The remote, for live television (`TV-4`).
 *
 * There is no channel zapping anywhere else in `src/tv` — no ch+/ch−, no
 * channel-number entry, no last-channel toggle — so this is where that model is
 * decided, once, for every template.
 *
 * ## Two rules carried over from `useFocusGrid`, and one of its own
 *
 *  1. **Handlers are read through a ref, never the closure.** One listener is
 *     bound and kept across renders; a handler captured in a closure would
 *     forever see the state of the render that bound it. That is not
 *     theoretical — it shipped once already as "OK does nothing".
 *  2. **Scoped to `scopeRef`.** Nothing fires unless focus is inside the
 *     screen, so a guide behind an overlay cannot eat the overlay's keys.
 *  3. **Exactly one owner per mode.** In `list` the arrows and OK belong to
 *     `useFocusGrid`, so this hook deliberately ignores them there and handles
 *     only what the grid has no idea about: the digits and ch+/ch−. Two
 *     listeners acting on one press is how a single ArrowDown moves two rows.
 *
 * ## ⚠️ Keys are `e.key`, never `keyCode`
 *
 * `lib/tizenKeys.ts` already translates Samsung's numeric codes in the capture
 * phase and claims the digits and colour keys from the firmware, so by the time
 * a press reaches here it carries a W3C name on all four platforms. Adding
 * keyCode handling here would double every press on Tizen.
 *
 * 🚩 **`ChannelUp` / `ChannelDown` are NOT in that translation table yet.**
 * Samsung emits them as keyCode 427/428, and `TIZEN_KEY_CODES` maps neither, so
 * on a Samsung remote today the dedicated ch+/ch− buttons do nothing and Up/Down
 * are what zap. Android TV and webOS emit the W3C names and work as written.
 * That gap is a two-line change in a file this unit does not own — see the PR.
 */

export type ZapMode = "watch" | "list" | "detail";

interface Args {
  scopeRef: RefObject<HTMLElement>;
  mode: ZapMode;
  /** ch+ / ch− and their arrow equivalents. `+1` is up the line-up. */
  onStep?: (delta: number) => void;
  /** A digit of a channel number. */
  onDigit?: (digit: string) => void;
  /** The last-channel toggle. */
  onLast?: () => void;
  /** Re-show the channel bar without changing channel. */
  onInfo?: () => void;
  /** Open the channel list. */
  onOpen?: () => void;
  /** Open the programme detail for the focused row. */
  onDetail?: () => void;
  /** Watch the channel this detail belongs to. */
  onWatch?: () => void;
  /** Leave — the screen in `watch`, the detail in `detail`. */
  onBack?: () => void;
  enabled?: boolean;
}

const UP = new Set(["ChannelUp", "PageUp"]);
const DOWN = new Set(["ChannelDown", "PageDown"]);

export function useZapKeys({
  scopeRef,
  mode,
  onStep,
  onDigit,
  onLast,
  onInfo,
  onOpen,
  onDetail,
  onWatch,
  onBack,
  enabled = true,
}: Args): void {
  const handlers = useRef({
    onStep,
    onDigit,
    onLast,
    onInfo,
    onOpen,
    onDetail,
    onWatch,
    onBack,
    mode,
  });
  useEffect(() => {
    handlers.current = {
      onStep,
      onDigit,
      onLast,
      onInfo,
      onOpen,
      onDetail,
      onWatch,
      onBack,
      mode,
    };
  });

  useEffect(() => {
    if (!enabled) return;
    function onKey(e: KeyboardEvent) {
      const el = scopeRef.current;
      if (!el) return;
      if (
        !el.contains(document.activeElement) &&
        document.activeElement !== el
      ) {
        return;
      }
      const h = handlers.current;
      const k = e.key;

      // Digits work in every mode. A guest who knows they want channel 12 must
      // not have to find their way back to a particular screen to type it.
      if (/^[0-9]$/.test(k)) {
        e.preventDefault();
        h.onDigit?.(k);
        return;
      }
      // ch+ / ch− likewise: the dedicated buttons mean the same thing wherever
      // the guest happens to be.
      if (UP.has(k)) {
        e.preventDefault();
        h.onStep?.(1);
        return;
      }
      if (DOWN.has(k)) {
        e.preventDefault();
        h.onStep?.(-1);
        return;
      }

      if (h.mode === "watch") {
        switch (k) {
          case "ArrowUp":
            e.preventDefault();
            h.onStep?.(1);
            return;
          case "ArrowDown":
            e.preventDefault();
            h.onStep?.(-1);
            return;
          case "ArrowLeft":
            e.preventDefault();
            h.onLast?.();
            return;
          case "ArrowRight":
            e.preventDefault();
            h.onInfo?.();
            return;
          case "Enter":
          case " ":
            e.preventDefault();
            h.onOpen?.();
            return;
          case "Escape":
          case "Backspace":
            e.preventDefault();
            h.onBack?.();
            return;
          default:
            return;
        }
      }

      if (h.mode === "list") {
        // Everything else in the list belongs to `useFocusGrid`.
        if (k === "ArrowRight") {
          e.preventDefault();
          h.onDetail?.();
        }
        return;
      }

      // detail
      switch (k) {
        case "Escape":
        case "Backspace":
        case "ArrowLeft":
          e.preventDefault();
          h.onBack?.();
          return;
        case "Enter":
        case " ":
          e.preventDefault();
          h.onWatch?.();
          return;
        default:
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [enabled, scopeRef]);
}
